import CloseIcon from '@atlaskit/icon/glyph/editor/close';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { FormattedRelative } from 'react-intl';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';

import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import SearchStore from '../../store/searchStore';
import YourWorkRow from '../YourWorkTabs/YourWorkRow';

import EmptyState from './EmptyState';
import StillNotFound from './StillNotFound';

const SearchContainer = styled.div`
  transition: width 0.4s ease-in-out;
  position: absolute;
  width: 240px;
  max-width: 50%;
  right: 0;
  top: 0;
`;

const SearchResultsContainer = styled.div`
  width: 100%;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
`;

const ActionButton = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  border: 0;
  background: transparent;
  opacity: 0.5;

  &:hover {
    opacity: 1;
  }
`;

const SpinnerWrapper = styled.div`
  position: absolute;
  top: 5px;
  right: 8px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding-left: 24px;
  line-height: 2.5;
  border: 2px solid ${colors.N40};
  border-radius: 3px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border: 2px solid ${colors.B400};
  }
`;

interface Props {
  searchStore: SearchStore;
}

interface State {
  query: string;
}

@observer
class Search extends React.Component<Props & InjectedIntlProps & AnalyticsConsumerProps, State> {
  state = {
    query: '',
  };

  private searchField: React.RefObject<HTMLInputElement>;

  constructor(props: Props & InjectedIntlProps & AnalyticsConsumerProps, state: State) {
    super(props, state);
    this.searchField = React.createRef<HTMLInputElement>();
  }

  componentDidMount() {
    const { analytics, searchStore } = this.props;
    when(
      () => searchStore.hasLoaded,
      () => {
        void analytics.track('search', 'viewed', {
          count: searchStore.nodes.length,
        });
      },
    );
  }

  escapeSearch = () => {
    this.setState(
      {
        query: '',
      },
      () => {
        const { current } = this.searchField;

        if (current) {
          current.blur();
        }

        const { searchStore } = this.props;
        searchStore.clearResults();
        searchStore.exitSearch();
      },
    );
  };

  onSearchFocus = () => {
    this.props.searchStore.enterSearch();
  };

  onSearchUnFocus = () => {
    if (this.state.query && this.state.query.length > 0) {
      return;
    }
    const { searchStore } = this.props;
    searchStore.clearResults();
    searchStore.exitSearch();
  };

  render() {
    const { searchStore } = this.props;
    const canShowResults = searchStore.isSearching && searchStore.hasLoaded;
    const hasResults = searchStore.nodes.length > 0;

    return (
      <React.Fragment>
        <SearchContainer>
          <SearchIconWrapper>
            <SearchIcon
              label={this.props.intl.formatMessage(messages.searchLabel)}
              primaryColor={colors.N200}
              size="small"
            />
          </SearchIconWrapper>

          {searchStore.hasLoaded && (
            <ActionButton onClick={this.escapeSearch}>
              <CloseIcon label="Cancel" primaryColor={colors.N500} />
            </ActionButton>
          )}

          {searchStore.isLoading && !searchStore.hasLoaded && (
            <SpinnerWrapper>
              <Spinner size="small" />
            </SpinnerWrapper>
          )}

          <SearchInput
            autoComplete="off"
            type="input"
            name="name"
            onChange={({ target }) => {
              this.search(target.value);
            }}
            onFocus={this.onSearchFocus}
            onBlur={this.onSearchUnFocus}
            value={this.state.query}
            innerRef={this.searchField}
            onKeyDown={({ keyCode }) => {
              if (keyCode === 27) {
                this.escapeSearch();
              }
            }}
          />
        </SearchContainer>
        {canShowResults && hasResults && this.renderSearchResults()}
        {canShowResults && this.renderNotFound(hasResults)}
      </React.Fragment>
    );
  }

  renderNotFound = (hasResults: boolean) => {
    if (hasResults) {
      return <StillNotFound searchTerm={this.state.query} />;
    }
    return <EmptyState searchTerm={this.state.query} />;
  };

  renderSearchResults = () => {
    const { searchStore } = this.props;

    if (searchStore.nodes.length === 0) {
      return null;
    }

    return (
      <SearchResultsContainer>
        <div data-test-id="search-result-row">
          {searchStore.nodes.map((item, index) => (
            <YourWorkRow
              analyticsAttributes={{
                tab: 'search',
              }}
              key={item.object.id}
              object={item.object}
              contributors={item.contributors}
              container={item.container}
              fullIndex={index}
            >
              <FormattedRelative value={item.timestamp} />
            </YourWorkRow>
          ))}
        </div>
      </SearchResultsContainer>
    );
  };

  search(name: string) {
    const { searchStore } = this.props;
    this.setState({
      query: name,
    });
    if (!name || name.length === 0) {
      searchStore.clearResults();
      return;
    }
    searchStore.search(name, 50);
  }
}

export default inject(({ searchStore }) => ({ searchStore }))(injectIntl(withAnalytics(Search)));

const messages = defineMessages({
  searchLabel: {
    defaultMessage: 'Search',
    description: 'label for search icon in the search input',
    id: 'uchi.searchLabel',
  },
});
